<script setup lang="ts">
import { type Component, computed } from "vue";

import PageIcon from "~/components/dumb/PageIcon.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { XIcon } from "~/icons";
import { IconSize, PageLinkMode } from "~/shared/enums";
import type { Page } from "~/shared/types";
import { getTitleOrNoTitle } from "~/utils/entityTitle";

const props = defineProps<{
  title: string;
  page?: Page;
  icon?: Component;
  iconArgs?: object;
  strikethrough?: boolean;
  mode?: PageLinkMode;
  muted?: boolean;
  notInText?: boolean;
  shrink?: boolean;
  disableHover?: boolean;
}>();

const emit = defineEmits<{
  remove: [];
  click: [event: MouseEvent];
  keydown: [event: KeyboardEvent];
  contextMenu: [MouseEvent];
}>();

const isNotificationMode = computed(() => props.mode === PageLinkMode.NOTIFICATION);
const isChatEnabled = computed(() => props.mode === PageLinkMode.CHAT_ENABLED);
const isChatDisabled = computed(() => props.mode === PageLinkMode.CHAT_DISABLED);
const displayTitle = computed(() => getTitleOrNoTitle(props.title));
</script>

<template>
  <div
    class="group/page-link relative cursor-pointer items-center rounded-sm px-1 text-md decoration-md"
    :class="{
      '!min-h-5': isChatEnabled || isChatDisabled || muted,
      'bg-opposite/5': !isNotificationMode && !isChatDisabled && !muted,
      'bg-opposite/10': isChatDisabled && !muted,
      'hover:bg-opposite/10': !isChatEnabled && !isChatDisabled && !muted && !disableHover,
      'inline-flex min-h-6 max-w-64 min-w-0 shrink gap-1.5': shrink,
      '!inline py-[3.5px] align-baseline': !shrink && (!notInText || muted),
    }"
    @contextmenu="emit('contextMenu', $event)"
    @click="emit('click', $event)"
    @keydown="emit('keydown', $event)">
    <PageIcon
      v-if="page && !muted"
      :page="page"
      :size="IconSize.S"
      :class="!shrink && 'mr-1.5 mb-0.5 inline-block align-middle'" />
    <component
      :is="icon"
      v-else-if="icon && !muted"
      v-bind="iconArgs"
      class="icon-sm"
      :class="!shrink && 'mr-1.5 mb-0.5 inline-block align-middle'" />
    <span
      :class="[
        shrink ? 'truncate' : 'break-words hyphens-auto',
        strikethrough && 'line-through',
        muted && 'text-lt underline hover:text-md',
        isNotificationMode && 'leading-6 whitespace-pre-line',
      ]">
      {{ displayTitle }}
    </span>
    <slot />
    <div
      v-if="isChatEnabled || $slots.actions"
      class="absolute top-1/2 right-0.5 hidden -translate-y-1/2 cursor-pointer items-center rounded-sm bg-lt group-hover/page-link:flex">
      <slot name="actions" />
      <button
        v-if="isChatEnabled"
        type="button"
        class="flex items-center justify-center rounded-sm p-0.5 hover:bg-md"
        aria-label="Remove from context"
        @click.stop="emit('remove')"
        @keydown.enter="emit('remove')">
        <Tooltip text="Remove from context">
          <XIcon class="icon-xs text-lt" />
        </Tooltip>
      </button>
    </div>
  </div>
</template>
